import React, { useState, useEffect, useCallback } from "react";
import { invoicingAPI } from "../../utils/invoicingAPI";
import { LoadingSpinner } from "../../components/Loaders";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiX,
  FiArrowLeft,
  FiGlobe,
  FiPercent,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./TaxSystems.css";

const TAX_TYPES = [
  { value: "VAT", label: "VAT (Value Added Tax)" },
  { value: "GST", label: "GST (Goods & Services Tax)" },
  { value: "SALES_TAX", label: "Sales Tax" },
  { value: "EXCISE", label: "Excise Duty" },
  { value: "OTHER", label: "Other" },
];

const EMPTY_FORM = {
  name: "",
  country: "",
  tax_name: "",
  tax_rate: "",
  tax_type: "VAT",
  is_active: true,
};

function TaxSystems() {
  const { t } = useTranslation();
  const [view, setView] = useState("list"); // list | create | edit
  const [taxSystems, setTaxSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadTaxSystems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await invoicingAPI.listTaxSystems();
      const data = response?.data || response;
      setTaxSystems(Array.isArray(data) ? data : data?.tax_systems || []);
      setError(null);
    } catch (err) {
      setError(err?.message || "Failed to load tax systems");
      setTaxSystems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTaxSystems();
  }, [loadTaxSystems]);

  const openCreateForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingId(null);
    setView("create");
  };

  const openEditForm = (ts) => {
    setFormData({
      name: ts.name || "",
      country: ts.country || "",
      tax_name: ts.tax_name || "",
      tax_rate: ts.tax_rate || "",
      tax_type: ts.tax_type || "VAT",
      is_active: ts.is_active !== false,
    });
    setEditingId(ts.id);
    setView("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.name.trim() ||
      !formData.country.trim() ||
      !formData.tax_rate
    ) {
      showToast.error("Name, country, and tax rate are required");
      return;
    }

    try {
      setFormLoading(true);
      const payload = {
        ...formData,
        tax_rate: parseFloat(formData.tax_rate),
      };

      if (editingId) {
        await invoicingAPI.updateTaxSystem(editingId, payload);
        showToast.success("Tax system updated successfully");
      } else {
        await invoicingAPI.createTaxSystem(payload);
        showToast.success("Tax system created successfully");
      }
      setView("list");
      loadTaxSystems();
    } catch (err) {
      showToast.error(err?.message || "Failed to save tax system");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (ts) => {
    const confirmed = await showConfirm({
      title: "Delete Tax System",
      message: `Are you sure you want to delete "${ts.name}"? Organizations using this system will need to select a new one.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });
    if (!confirmed) return;

    try {
      await invoicingAPI.deleteTaxSystem(ts.id);
      showToast.success("Tax system deleted");
      loadTaxSystems();
    } catch (err) {
      showToast.error(
        err?.message || "Failed to delete tax system (may be in use)",
      );
    }
  };

  const handleToggleActive = async (ts) => {
    try {
      await invoicingAPI.updateTaxSystem(ts.id, { is_active: !ts.is_active });
      showToast.success(
        ts.is_active ? "Tax system deactivated" : "Tax system activated",
      );
      loadTaxSystems();
    } catch (err) {
      showToast.error(err?.message || "Failed to update tax system");
    }
  };

  // ─── Form View ────────────────────────────────────────────────
  if (view === "create" || view === "edit") {
    return (
      <div className="page-content tax-systems-page">
        <div className="section-header">
          <button className="btn btn-ghost" onClick={() => setView("list")}>
            <FiArrowLeft /> Back
          </button>
          <h2>{editingId ? "Edit Tax System" : "Create Tax System"}</h2>
        </div>

        <form className="tax-form card" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>System Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g. Rwanda VAT"
                required
              />
            </div>
            <div className="form-group">
              <label>Country *</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
                placeholder="e.g. Rwanda"
                required
              />
            </div>
            <div className="form-group">
              <label>Tax Name</label>
              <input
                type="text"
                value={formData.tax_name}
                onChange={(e) =>
                  setFormData({ ...formData, tax_name: e.target.value })
                }
                placeholder="e.g. VAT"
              />
            </div>
            <div className="form-group">
              <label>Tax Rate (%) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.tax_rate}
                onChange={(e) =>
                  setFormData({ ...formData, tax_rate: e.target.value })
                }
                placeholder="e.g. 18"
                required
              />
            </div>
            <div className="form-group">
              <label>Tax Type</label>
              <select
                value={formData.tax_type}
                onChange={(e) =>
                  setFormData({ ...formData, tax_type: e.target.value })
                }
              >
                {TAX_TYPES.map((tt) => (
                  <option key={tt.value} value={tt.value}>
                    {tt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setView("list")}
              disabled={formLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? "Saving..." : editingId ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────
  return (
    <div className="page-content tax-systems-page">
      <div className="section-header">
        <h2>
          <FiGlobe style={{ marginRight: 8 }} />
          Tax Systems
        </h2>
        <button className="btn btn-primary" onClick={openCreateForm}>
          <FiPlus /> Add Tax System
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} onRetry={loadTaxSystems} />
      ) : taxSystems.length === 0 ? (
        <EmptyState
          title="No Tax Systems"
          message="Create your first tax system to enable invoicing."
          action={
            <button className="btn btn-primary" onClick={openCreateForm}>
              <FiPlus /> Add Tax System
            </button>
          }
        />
      ) : (
        <div className="tax-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Country</th>
                <th>Tax Name</th>
                <th>Rate</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taxSystems.map((ts) => (
                <tr key={ts.id}>
                  <td className="fw-600">{ts.name}</td>
                  <td>{ts.country}</td>
                  <td>{ts.tax_name || "—"}</td>
                  <td>
                    <span className="tax-rate-badge">
                      <FiPercent size={12} /> {ts.tax_rate}%
                    </span>
                  </td>
                  <td>
                    <span
                      className={`type-badge type-${ts.tax_type?.toLowerCase()}`}
                    >
                      {ts.tax_type}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${ts.is_active ? "active" : "inactive"}`}
                      onClick={() => handleToggleActive(ts)}
                      title={ts.is_active ? "Deactivate" : "Activate"}
                    >
                      {ts.is_active ? <FiCheck /> : <FiX />}
                      {ts.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-icon btn-ghost"
                        onClick={() => openEditForm(ts)}
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn btn-icon btn-danger-ghost"
                        onClick={() => handleDelete(ts)}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TaxSystems;
